import React from 'react';
import { motion } from 'framer-motion';

import { Box, Text } from '../../index';
import { validators } from '../../utils/validation';

interface PasswordStrengthProps {
  password: string;
  showCriteria?: boolean;
  minStrength?: number;
  onStrengthChange?: (strength: number) => void;
}

export function PasswordStrength({
  password,
  showCriteria = true,
  minStrength = 3,
  onStrengthChange,
}: PasswordStrengthProps) {
  const result = validators.strongPassword(password);
  const criteria = result.criteria || {
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    isLongEnough: false,
  };

  // Calculate strength score (0-5)
  const strength = Object.values(criteria).filter(Boolean).length;

  React.useEffect(() => {
    onStrengthChange?.(strength);
  }, [strength, onStrengthChange]);

  const getStrengthLabel = () => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Çok Zayıf', color: '#DC2626' };
      case 2:
        return { label: 'Zayıf', color: '#F59E0B' };
      case 3:
        return { label: 'Orta', color: '#EAB308' };
      case 4:
        return { label: 'Güçlü', color: '#10B981' };
      case 5:
        return { label: 'Çok Güçlü', color: '#059669' };
      default:
        return { label: '', color: '#E5E7EB' };
    }
  };

  const { label, color } = getStrengthLabel();
  const progressValue = (strength / 5) * 100;

  const criteriaItems = [
    { key: 'isLongEnough', label: 'En az 8 karakter', met: criteria.isLongEnough },
    { key: 'hasLowerCase', label: 'Küçük harf', met: criteria.hasLowerCase },
    { key: 'hasUpperCase', label: 'Büyük harf', met: criteria.hasUpperCase },
    { key: 'hasNumber', label: 'Rakam', met: criteria.hasNumber },
    { key: 'hasSpecialChar', label: 'Özel karakter (!@#$%)', met: criteria.hasSpecialChar },
  ];

  return (
    <Box marginTop="8px">
      {password && (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="4px">
            <Text fontSize="12px" color="#6B7280">
              Şifre Gücü
            </Text>
            <Text fontSize="12px" fontWeight="500" style={{ color }}>
              {label}
            </Text>
          </Box>

          <Box
            position="relative"
            height="4px"
            backgroundColor="#E5E7EB"
            borderRadius="2px"
            overflow="hidden"
          >
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                backgroundColor: color,
                borderRadius: '2px',
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progressValue}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </Box>

          {showCriteria && (
            <Box marginTop="12px">
              {criteriaItems.map((item) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ marginBottom: '4px' }}
                >
                  <Text
                    fontSize="12px"
                    color={item.met ? '#10B981' : '#9CA3AF'}
                    display="flex"
                    alignItems="center"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      style={{ marginRight: '6px' }}
                    >
                      {item.met ? (
                        <path d="M10.97 4.97a.75.75 0 011.07 1.05l-3.99 4.99a.75.75 0 01-1.08.02L4.324 8.384a.75.75 0 111.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 01.02-.022z" />
                      ) : (
                        <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z" />
                      )}
                    </svg>
                    {item.label}
                  </Text>
                </motion.div>
              ))}
            </Box>
          )}

          {strength < minStrength && (
            <Text fontSize="12px" color="#DC2626" marginTop="8px">
              Şifre en az "
              {criteriaItems
                .slice(0, minStrength)
                .map((i) => i.label)
                .join(', ')}
              " içermelidir.
            </Text>
          )}
        </>
      )}
    </Box>
  );
}
